<script setup>
import { ref } from 'vue';
import { debounce } from '@chatwoot/utils';
import KnowledgeBaseLoader from './KnowledgeBaseLoader.vue';
import KnowledgeArticle from './KnowledgeArticle.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useStore } from 'dashboard/composables/store';

defineProps({
  articles: {
    type: Array,
    default: () => [],
  },
  isFetchingArticle: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['searchArticles', 'refresh']);

const store = useStore();
const setTextBoxToArticle = article => {
  store.dispatch('setSelectedKnowledgeBaseArticle', article);
};

const chatContainer = ref(null);

const searchParam = ref('');
const debouncedEmit = debounce(() => {
  emit('searchArticles', searchParam.value);
}, 300);

const refreshArticles = () => {
  searchParam.value = '';
  emit('refresh');
};
</script>

<template>
  <div class="flex gap-4 items-center pr-4 pt-4">
    <div class="relative pl-4 search-wrapper flex-grow-0 w-full">
      <input
        v-model="searchParam"
        type="text"
        :placeholder="$t('KB_MGMT.AGENT_USAGE.SEARCH_PLACEHOLDER')"
        class="search-input"
        @input="debouncedEmit"
      />
      <fluent-icon
        icon="search"
        class="search--icon text-n-slate-11 absolute top-0 bottom-0 my-auto left-5"
        size="20"
      />
    </div>
    <NextButton
      id="refreshKnowledgeBase"
      v-tooltip.top-end="$t('KB_MGMT.AGENT_USAGE.REFRESH')"
      icon="i-lucide-refresh-cw"
      slate
      md
      faded
      class="flex-shrink-0"
      @click="refreshArticles"
    />
  </div>
  <div
    class="flex flex-col text-sm leading-6 tracking-tight overflow-y-auto h-[90vh] pb-28"
  >
    <div
      v-if="!isFetchingArticle && articles.length"
      ref="chatContainer"
      class="flex-1 px-4 py-4 space-y-6"
    >
      <KnowledgeArticle
        v-for="articleItem in articles"
        :key="articleItem.id"
        :item="articleItem"
        @send-message="setTextBoxToArticle"
      />
    </div>
    <KnowledgeBaseLoader
      v-if="isFetchingArticle"
      class="flex flex-col items-center justify-center h-full text-base text-slate-600 dark:text-slate-300 py-8"
    />
    <p
      v-if="!isFetchingArticle && !articles.length"
      class="flex flex-col items-center justify-center h-full text-base text-slate-600 dark:text-slate-300 py-8"
    >
      {{ $t('KB_MGMT.AGENT_USAGE.NO_RESULTS') }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
@import 'widget/assets/scss/variables.scss';
@import 'dashboard/assets/scss/mixins.scss';
.search-wrapper {
  .search-input {
    padding-left: 27px;
    font-size: $font-size-small;
    margin: 0;
  }
  &:hover {
    .search--icon,
    .search--label {
      @apply hover:text-woot-500 dark:hover:text-woot-500;
    }
  }
}
</style>
